import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CELESTE } from "./sidebarTheme";

const isActivePath = (pathname, to, end) => {
  if (!to) return false;
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
};

function SidebarItem({ icon, label, to, end, children, collapsed, onNavigate }) {
  const hasChildren = Array.isArray(children) && children.length > 0;
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const childActive =
    hasChildren && children.some((c) => isActivePath(pathname, c.to, false));
  const active = hasChildren ? childActive : isActivePath(pathname, to, end);

  const [open, setOpen] = useState(childActive);

  // Abre el submenú automáticamente cuando navegas a una de sus rutas.
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  const handleRowClick = () => {
    if (hasChildren) {
      setOpen((v) => !v);
      return;
    }
    if (to) {
      navigate(to);
      onNavigate?.();
    }
  };

  // Estilos base segun estado (activo / inactivo).
  const rowBg = active ? CELESTE.bg : "transparent";
  const rowBorder = active ? `1px solid ${CELESTE.border}` : "1px solid transparent";
  const labelColor = active ? CELESTE.accent : "#475569";
  const iconColor = active ? CELESTE.accentDark : "#94a3b8";

  const onEnter = (e) => {
    if (active) return;
    e.currentTarget.style.background = CELESTE.bgSoft;
    const ic = e.currentTarget.querySelector(".icon");
    const lb = e.currentTarget.querySelector(".label");
    if (ic) ic.style.color = CELESTE.accentDark;
    if (lb) lb.style.color = "#1e293b";
  };

  const onLeave = (e) => {
    if (active) return;
    e.currentTarget.style.background = "transparent";
    const ic = e.currentTarget.querySelector(".icon");
    const lb = e.currentTarget.querySelector(".label");
    if (ic) ic.style.color = iconColor;
    if (lb) lb.style.color = labelColor;
  };

  return (
    <div style={{ marginBottom: "4px" }}>
      <button
        type="button"
        onClick={handleRowClick}
        title={collapsed ? label : undefined}
        style={{
          position: "relative",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          textAlign: "left",
          gap: collapsed ? "0px" : "12px",
          padding: collapsed ? "11px 10px" : "10px 12px",
          borderRadius: "10px",
          cursor: "pointer",
          background: rowBg,
          border: rowBorder,
          color: labelColor,
          transition: "background 0.18s ease, color 0.18s ease",
          outline: "none",
        }}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {active && !collapsed && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-14px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "4px",
              height: "22px",
              borderRadius: "0 4px 4px 0",
              background: CELESTE.accentDark,
            }}
          />
        )}

        <span
          className="icon"
          style={{
            display: "flex",
            alignItems: "center",
            transition: "color 0.18s ease",
            color: iconColor,
            flex: "0 0 auto",
          }}
        >
          {icon}
        </span>

        {!collapsed && (
          <span
            className="label"
            style={{
              color: labelColor,
              fontWeight: active ? "700" : "600",
              fontSize: "14.5px",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transition: "color 0.18s ease",
            }}
          >
            {label}
          </span>
        )}

        {!collapsed && hasChildren && (
          <span
            style={{
              fontSize: "12px",
              color: active ? CELESTE.accentDark : "#94a3b8",
              flex: "0 0 auto",
              transition: "transform 0.2s ease",
              transform: open ? "rotate(90deg)" : "none",
            }}
          >
            ▸
          </span>
        )}
      </button>

      {!collapsed && hasChildren && open && (
        <div
          style={{
            marginLeft: "26px",
            marginTop: "4px",
            marginBottom: "4px",
            borderLeft: `1px solid ${CELESTE.borderSubmenu}`,
            paddingLeft: "10px",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          }}
        >
          {children.map((child, index) => {
            const cActive = isActivePath(pathname, child.to, false);
            return (
              <Link
                key={index}
                to={child.to}
                onClick={() => onNavigate?.()}
                style={{
                  fontSize: "13.5px",
                  color: cActive ? CELESTE.accent : "#64748b",
                  textDecoration: "none",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  transition: "0.18s",
                  fontWeight: cActive ? "700" : "600",
                  background: cActive ? CELESTE.bg : "transparent",
                  display: "block",
                }}
                onMouseEnter={(e) => {
                  if (cActive) return;
                  e.currentTarget.style.background = CELESTE.bgSoft;
                  e.currentTarget.style.color = "#1e293b";
                }}
                onMouseLeave={(e) => {
                  if (cActive) return;
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SidebarItem;
